import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Plus, Minus } from "lucide-react";

const faqs = [
  {
    question: "What can I expect during our first session?",
    answer:
      "Our first session focuses on understanding you. We’ll discuss your health goals, current lifestyle, medical history, eating habits, and any challenges you face. We’ll assess your needs and guide you through the steps we’ll take together. You’ll leave with clarity, direction, and an actionable starting plan.",
  },
  {
    question: "Do we offer customised diet plans?",
    answer:
      "Yes. Every plan is tailor-made based on your goals, routine, preferences, medical needs, and food availability. You’ll receive a practical, sustainable plan—no extreme diets or complicated recipes.",
  },
  {
    question: "Are our programs suitable for people with medical conditions?",
    answer:
      "Absolutely. As a team of qualified dietician, we regularly work with clients who have medical concerns. Plans are created to be safe, appropriate, and aligned with your doctor’s recommendations. Your health and comfort come first.",
  },
  {
    question: "How soon can I expect results?",
    answer:
      "Results vary from person to person, but most clients notice positive changes in energy, digestion, and mood within the first 1–2 weeks. Visible physical changes usually follow with consistency. Our approach focuses on long-term, steady progress—not quick fixes.",
  },
  {
    question: "What is our cancellation or rescheduling policy?",
    answer:
      "We understand life happens. You may reschedule up to 24 hours before your session without any fee. Cancellations or last-minute rescheduling (within 24 hours) will be subject to the standard policy, as it protects time reserved exclusively for you.",
  },
  {
    question: "What payment methods do we accept?",
    answer:
      "You can pay through UPI, bank transfer, and major digital wallets. If you need an invoice or receipt, we are happy to provide one.",
  },
  {
    question: "Do we offer online consultations or virtual sessions?",
    answer:
      "Yes. We offer online consultations through video call for clients across different cities or those who prefer the comfort of home. Virtual sessions are equally detailed and effective.",
  },
  {
    question: "Do we provide support after the session or program ends?",
    answer:
      "Yes, we offer follow-up support options. Depending on your program, this may include check-ins, progress reviews, chat support, or guidance for maintaining results even after your plan ends.",
  },
  {
    question: "What makes our approach different from others?",
    answer:
      "Our approach blends evidence-based nutrition with practical lifestyle strategies. We focus on *real-life diets*, not restrictive charts. We consider your routines, stress levels, medical needs, and emotional relationship with food. You get personalised guidance, continuous support, and a balanced plan that fits seamlessly into your daily life.",
  },
];

const FAQItem = ({
  question,
  answer,
  isOpen,
  onClick,
}: {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
}) => {
  return (
    <motion.div
      initial={false}
      className="border-b border-border/50 last:border-0"
    >
      <button
        onClick={onClick}
        className="w-full py-6 flex items-center justify-between text-left focus:outline-none group"
      >
        <span className="text-lg font-medium text-foreground group-hover:text-primary transition-colors duration-300">
          {question}
        </span>
        <span className="ml-4 text-muted-foreground group-hover:text-primary transition-colors duration-300">
          {isOpen ? <Minus size={20} /> : <Plus size={20} />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <p className="pb-6 text-muted-foreground leading-relaxed">
              {answer}
            </p>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const FAQ = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  return (
    <section id="faq" className="py-32 bg-background">
      <div className="container mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-24">
          {/* Header */}
          <div className="lg:col-span-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="sticky top-32"
            >
              <span className="text-xs font-bold tracking-[0.2em] text-muted-foreground uppercase mb-4 block">
                Common Questions
              </span>
              <h2 className="font-serif text-4xl md:text-5xl font-semibold text-foreground mb-6 leading-tight">
                Curious? <br />
                <span className="italic text-muted-foreground">
                  Let's clear things up.
                </span>
              </h2>
              <p className="text-lg text-muted-foreground font-light leading-relaxed mb-8">
                Here are some of the most frequent questions we get. If you
                don't see yours here, feel free to reach out directly.
              </p>
            </motion.div>
          </div>

          {/* FAQ List */}
          <div className="lg:col-span-8">
            <div className="bg-white/40 backdrop-blur-md rounded-3xl p-8 md:p-12 border border-white/20 shadow-sm hover:shadow-md transition-shadow duration-300">
              {faqs.map((faq, index) => (
                <FAQItem
                  key={index}
                  question={faq.question}
                  answer={faq.answer}
                  isOpen={openIndex === index}
                  onClick={() =>
                    setOpenIndex(openIndex === index ? null : index)
                  }
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
